import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import { AppDispatch, RootState } from "../../../store/store";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { header2Styles } from "../../../assets/styles/header2";
import { selectState } from "../../../store/slices/themeSlice";
import { cancelPartnerClientId } from "../../../store/actions/partnerClientIdsActions";
import { resetState } from "../../../store/slices/partnerClientIdsSlice";

interface CancelClientIdModalProps {
  isOpen: boolean;
  target?: {
    id: string;
    clientId: string;
    businessId?: string;
    users_count?: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
}

const CancelClientIdModal: React.FC<CancelClientIdModalProps> = ({
  isOpen,
  target,
  onConfirm,
  onCancel,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(header2Styles, theme);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector(
    (state: RootState) => state.partnerClientIds_slice
  );

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
        dispatch(resetState());
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onCancel, dispatch]);

  if (!isOpen || !target) return null;

  const userCount = target.users_count ?? 0;
  const hasUsers = userCount > 0;

  const handleConfirm = async () => {
    try {
      await dispatch(
        cancelPartnerClientId({
          row: {
            id: target.id,
            businessId: target.businessId,
            clientId: target.clientId,
          },
        })
      ).unwrap();
      onConfirm();
    } catch (err) {
      console.error("Error canceling client ID:", err);
    }
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.modal} ref={modalRef}>
        <h2 className={classes.headerTextLogOutModal}>
          CANCEL CLIENT ID &ldquo;{target.clientId}&rdquo;
        </h2>

        {hasUsers ? (
          <div style={{ lineHeight: 1.5 }}>
            <p>
              This client ID currently has <strong>{userCount}</strong> user
              {userCount === 1 ? "" : "s"}. Canceling it will:
            </p>
            <ul style={{ marginTop: 4, paddingLeft: 20 }}>
              <li>freeze the client ID so no one else can sign up through it;</li>
              <li>
                move every one of those {userCount} user
                {userCount === 1 ? "" : "s"} back to the <strong>elisyan</strong>{" "}
                business;
              </li>
              <li>revoke each user&rsquo;s premium entitlement;</li>
              <li>unassign each user&rsquo;s advisors;</li>
              <li>
                issue one replacement referral code for the affected users, with
                their old access codes marked as canceled.
              </li>
            </ul>
            <p>
              <strong>This cannot be undone.</strong>
            </p>
          </div>
        ) : (
          <p style={{ lineHeight: 1.5 }}>
            This client ID has no users. Canceling it will retire it so no one
            can sign up through it. This cannot be undone.
          </p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            marginTop: 16,
          }}
        >
          <CustomButton
            title={hasUsers ? "CONFIRM CANCEL & RESET USERS" : "CONFIRM CANCEL"}
            name="confirmCancelClientIdButton"
            type="button"
            handleClick={handleConfirm}
            loading={loading}
            disabled={loading}
            variant="danger"
          />
          <CustomButton
            title="KEEP CLIENT ID"
            name="keepClientIdButton"
            type="button"
            handleClick={() => {
              onCancel();
              dispatch(resetState());
            }}
            loading={false}
            disabled={loading}
            variant="secondary"
          />
        </div>

        {error && <p className={classes.errorText}>{error}</p>}
      </div>
    </div>
  );
};

export default CancelClientIdModal;
